import { CreateTeamDialog } from "./-components/create-team-dialog"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation } from "convex/react"
import type { Doc, Id } from "@convex/_generated/dataModel"
import { api } from "@convex/_generated/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Users, ChevronRight, Mail, Check, X } from "lucide-react"
import { toast } from "sonner"
import { isCustomConvexError } from "@convex/utils"

export const Route = createFileRoute("/(app)/app/teams/")({
  component: RouteComponent,
})

function RouteComponent() {
  const currentUser = useQuery(api.users.handlers.current)
  const userTeamsData = useQuery(
    api.usersToTeams.handlers.listByUser,
    currentUser ? { userId: currentUser._id } : "skip"
  )
  const pendingInvites = useQuery(
    api.usersToTeams.handlers.getPendingInvitesForUser
  )

  if (
    !currentUser ||
    userTeamsData === undefined ||
    pendingInvites === undefined
  ) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    )
  }

  // Filter out memberships with null teams
  const teamsWithData = userTeamsData.filter((m) => m.team !== null)

  const hasNoTeamsOrInvites =
    teamsWithData.length === 0 && pendingInvites.length === 0

  if (hasNoTeamsOrInvites) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">My Teams</h1>
          <CreateTeamDialog />
        </div>
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <Users className="h-16 w-16 mx-auto mb-4 opacity-50 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              You are not a member of any teams yet.
            </p>
            <p className="text-sm text-muted-foreground">
              Create your own team or wait for an invitation to join one
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Teams</h1>
        </div>
        <CreateTeamDialog />
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Pending Invitations</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pendingInvites.map((invite) => {
              if (!invite.team) return null
              return <PendingInviteCard key={invite._id} invite={invite} />
            })}
          </div>
        </div>
      )}

      {/* Active Teams */}
      {teamsWithData.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">
            {pendingInvites.length > 0 ? "My Teams" : ""}
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {teamsWithData.map((membership) => {
              if (!membership.team) return null
              return (
                <TeamCard key={membership.team._id} team={membership.team} />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function PendingInviteCard({
  invite,
}: {
  invite: {
    _id: Id<"usersToTeams">
    team: Doc<"teams"> | null
  }
}) {
  const navigate = useNavigate()
  const acceptInvite = useMutation(api.usersToTeams.handlers.acceptTeamInvite)
  const declineInvite = useMutation(api.usersToTeams.handlers.declineTeamInvite)

  const handleAccept = async () => {
    try {
      const result = await acceptInvite({ membershipId: invite._id })
      toast.success("Invitation accepted!")
      // Navigate to team page
      if (result.teamId) {
        await navigate({
          to: "/app/teams/$teamId",
          params: { teamId: result.teamId },
        })
      }
    } catch (error) {
      toast.error(
        isCustomConvexError(error)
          ? error.data.message
          : "Failed to accept invitation"
      )
    }
  }

  const handleDecline = async () => {
    try {
      await declineInvite({ membershipId: invite._id })
      toast.success("Invitation declined")
    } catch (error) {
      toast.error(
        isCustomConvexError(error)
          ? error.data.message
          : "Failed to decline invitation"
      )
    }
  }

  if (!invite.team) return null

  return (
    <Card className="border-dashed border-2">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-blue-500" />
              {invite.team.name}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="capitalize">
                {invite.team.type}
              </Badge>
              <Badge variant="outline">Pending</Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          You've been invited to join this team
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1"
            onClick={() => {
              void handleAccept()
            }}
          >
            <Check className="h-4 w-4 mr-2" />
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => {
              void handleDecline()
            }}
          >
            <X className="h-4 w-4 mr-2" />
            Decline
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function TeamCard({ team }: { team: Doc<"teams"> }) {
  const teamMembers = useQuery(api.teams.handlers.getTeamMembers, {
    teamId: team._id,
  })
  const teamStats = useQuery(api.teams.handlers.getTeamStatsPreview, {
    teamId: team._id,
  })
  const recentActivity = useQuery(
    api.quizState.handlers.getRecentActivityForTeam,
    {
      teamId: team._id,
    }
  )

  const memberCount = teamMembers?.length ?? 0

  return (
    <Link
      to="/app/teams/$teamId"
      params={{ teamId: team._id }}
      className="block"
    >
      <Card className="hover:border-primary transition-colors cursor-pointer h-full">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2 mb-2">
                {team.name}
              </CardTitle>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>
              {memberCount} {memberCount === 1 ? "member" : "members"}
            </span>
          </div>

          {/* Team Stats Preview */}
          {teamStats && teamStats.totalQuizzes > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                Team Stats
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-muted-foreground">Completed</p>
                  <p className="text-sm font-medium">
                    {teamStats.totalQuizzes}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Avg Score</p>
                  <p className="text-sm font-medium">
                    {teamStats.averageScore.toFixed(1)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {recentActivity && recentActivity.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                Recent Activity
              </p>
              <div className="space-y-1">
                {recentActivity.slice(0, 2).map((activity) => (
                  <div
                    key={activity._id}
                    className="text-xs text-muted-foreground flex items-center justify-between gap-2"
                  >
                    <span className="truncate flex-1">{activity.quizName}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge
                        variant={
                          activity.status === "reviewed"
                            ? "default"
                            : activity.status === "submitted"
                              ? "secondary"
                              : "outline"
                        }
                        className="text-[10px] px-1.5 py-0"
                      >
                        {activity.status}
                      </Badge>
                      {activity.score !== undefined && (
                        <span className="font-medium">{activity.score}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}
